import { useEffect, useId, useMemo, useRef, useState } from "react"
import { GitPullRequest } from "lucide-react"
import { LoadingOrb } from "./LoadingOrb"

export const pullRequestCandidateRoleLabels = {
  minimal_direct: "Minimal direct",
  reuse_or_deletion: "Reuse or deletion",
  conventional_robust: "Conventional robust"
}

export const PULL_REQUEST_WORKFLOW_STEPS = [
  { key: "triage", label: "Triage", phases: [ "triage" ] },
  { key: "explore", label: "Explore solutions", phases: [ "candidate_generation" ] },
  { key: "verify", label: "Verify candidates", phases: [ "candidate_verification" ] },
  { key: "simplify", label: "Simplify", phases: [ "simplification", "simplified_verification" ] },
  { key: "select", label: "Select the minimal patch", phases: [ "selection" ] },
  { key: "final", label: "Final check", phases: [ "final_verification" ] },
  { key: "publish", label: "Publish", phases: [] }
]

const CANDIDATE_LANES = [
  { id: "candidate-1", role: "minimal_direct" },
  { id: "candidate-2", role: "reuse_or_deletion" },
  { id: "candidate-3", role: "conventional_robust" }
]

const WORKFLOW_LAYOUT = {
  defaultWidth: 608,
  maximumWidth: 760,
  minimumWidth: 280,
  compactBelow: 360,
  narrowBelow: 480,
  padding: 16,
  gap: 28,
  rowGap: 16,
  phaseHeight: 54,
  candidateHeight: 88,
  compactCandidateHeight: 56
}

const noPullRequestOutcomeLabels = {
  ALREADY_FIXED: "No pull request was needed.",
  CONFIGURATION_OR_USAGE_ERROR: "No code change was needed.",
  INSUFFICIENT_EVIDENCE: "No pull request was created.",
  DUPLICATE_OR_STALE_ISSUE: "No pull request was needed."
}

const PREPARATION_LABELS = {
  pull_request_generation_queued: "Waiting for agent capacity",
  agent_capacity_waiting: "Waiting for agent capacity",
  ec2_spot_runtime_allocating: "Allocating Spot runtime",
  ec2_spot_runtime_starting: "Booting runner",
  runtime_starting: "Booting runner",
  hosted_runner_booted: "Establishing network boundary",
  hosted_network_boundary: "Establishing network boundary",
  hosted_network_boundary_accepted: "Cloning repository",
  pull_request_generation_started: "Cloning repository",
  pull_request_repository_cloned: "Preparing worktree",
  pull_request_branch_checked_out: "Indexing codebase",
  provider_codebase_index_started: "Indexing codebase",
  provider_codebase_index_completed: "Starting triage"
}

function eventRawType(event) {
  return event?.raw_type || event?.rawType || ""
}

function eventCandidateId(event) {
  return event?.candidate_id || event?.candidateId || null
}

function firstLine(text, limit = 96) {
  const line = String(text || "").split("\n").map((part) => part.trim()).find(Boolean) || ""
  return line.length > limit ? `${line.slice(0, limit - 1)}…` : line
}

function candidateNodeState(candidate, selectedId) {
  if (candidate.id === selectedId) return "selected"
  if ([ "failed", "generation_failed", "verification_failed" ].includes(candidate.status)) return "rejected"
  if (candidate.status === "duplicate") return "duplicate"
  if ([ "passed", "verified" ].includes(candidate.status)) return "verified"
  if (candidate.status === "unavailable") return "unverified"
  if (candidate.status === "generated") return "generated"
  if (candidate.generating) return "generating"
  return "pending"
}

function candidateStatusLine(candidate, state) {
  switch (state) {
    case "selected": return "Selected — most semantically minimal"
    case "rejected": return firstLine(candidate.summary) ? `Rejected — ${firstLine(candidate.summary)}` : "Rejected"
    case "duplicate": return "Duplicate — merged with another option"
    case "verified": return "Verified"
    case "unverified": return "Verification unavailable"
    case "generated": return "Draft ready"
    case "generating": return "Drafting this approach…"
    default: return "Queued"
  }
}

function phaseStatusLine(state, { active, done, pending = "Queued", timedOut = false }) {
  if (state === "done") return done
  if (state === "active") return active
  if (state === "failed") return timedOut ? "Timed out" : "Stopped"
  if (state === "skipped") return "Not needed"
  return pending
}

export function buildPullRequestWorkflowModel(events = [], pullRequest = {}, { width = WORKFLOW_LAYOUT.defaultWidth } = {}) {
  const ordered = [...events].sort((a, b) => Number(a?.sequence || 0) - Number(b?.sequence || 0))
  const phasesStarted = new Set()
  const phasesCompleted = new Set()
  const phasesTimedOut = new Set()
  const candidates = new Map()
  let selectedId = null
  let committed = false
  let pushed = false
  let publishedEvent = false
  let publicationStarted = false
  let publicationFailed = false
  let failed = false
  let failureText = ""
  let noPrOutcome = null
  let noPrDiagnosis = ""
  let preparation = null

  const candidate = (candidateId, role) => {
    const existing = candidates.get(candidateId) || { id: candidateId, role: null, summary: "", status: null, generating: false }
    if (role) existing.role = role
    candidates.set(candidateId, existing)
    return existing
  }

  ordered.forEach((event) => {
    const rawType = eventRawType(event)
    const phase = event?.phase
    const candidateId = eventCandidateId(event)
    const role = event?.candidate_role || event?.candidateRole || null
    const verificationStatus = event?.verification_status || event?.verificationStatus || null

    if (PREPARATION_LABELS[rawType] && phasesStarted.size === 0) {
      preparation = { rawType, label: PREPARATION_LABELS[rawType], occurredAt: event?.occurred_at || event?.occurredAt || null }
    }

    if (rawType === "pull_request_pipeline_phase_started" && phase) {
      phasesStarted.add(phase)
      if (phase === "candidate_generation" && candidateId) candidate(candidateId, role).generating = true
    }
    if (rawType === "pull_request_pipeline_phase_completed" && phase) {
      phasesCompleted.add(phase)
      if (phase === "selection" && candidateId) selectedId = candidateId
    }
    if (rawType === "pull_request_pipeline_phase_timed_out" && phase) {
      phasesStarted.add(phase)
      phasesTimedOut.add(phase)
    }
    if ((rawType === "pull_request_pipeline_candidate_summary" || rawType === "pull_request_pipeline_verification") && candidateId) {
      const entry = candidate(candidateId, role)
      if (event?.summary) entry.summary = event.summary
      if (verificationStatus) entry.status = verificationStatus
    }
    if (rawType === "pull_request_changes_committed") committed = true
    if (rawType === "pull_request_branch_pushed") pushed = true
    if (rawType === "pull_request_publication_started" || rawType === "pull_request_publication_retrying") publicationStarted = true
    if (rawType === "pull_request_created") publishedEvent = true
    if (rawType === "pull_request_publication_failed") publicationFailed = true
    if (event?.type === "error") {
      failed = true
      failureText = firstLine(event?.text) || failureText
    }
    if (rawType === "pull_request_generation_no_pr_completed") {
      noPrOutcome = event?.outcome || noPrOutcome
      noPrDiagnosis = firstLine(event?.diagnosis) || noPrDiagnosis
    }
  })

  const outcome = noPrOutcome || (pullRequest.finishedAt ? pullRequest.outcome : null)
  const diagnosis = noPrDiagnosis || firstLine(pullRequest.diagnosis)
  const published = publishedEvent || (pullRequest.status === "ready" && Boolean(pullRequest.url))
  const runFailed = failed || pullRequest.status === "failed"

  const phaseActivityAfter = (stepIndex) => {
    return PULL_REQUEST_WORKFLOW_STEPS.slice(stepIndex + 1).some((step) => (
      step.phases.some((phase) => phasesStarted.has(phase) || phasesCompleted.has(phase))
    )) || (stepIndex < 6 && (committed || pushed || publicationStarted || publishedEvent))
  }

  const stepStates = PULL_REQUEST_WORKFLOW_STEPS.map((step, index) => {
    if (step.key === "publish") {
      if (published) return "done"
      if (outcome) return "skipped"
      if (committed || pushed || publicationStarted || phasesCompleted.has("final_verification")) return runFailed ? "failed" : "active"
      return "pending"
    }
    const started = step.phases.some((phase) => phasesStarted.has(phase))
    const completed = step.phases.some((phase) => phasesCompleted.has(phase))
    if (completed || phaseActivityAfter(index)) return "done"
    if (started) return runFailed ? "failed" : "active"
    if (outcome) return "skipped"
    return "pending"
  })

  if (outcome) {
    PULL_REQUEST_WORKFLOW_STEPS.forEach((step, index) => {
      if (index > 0 && stepStates[index] !== "done") stepStates[index] = "skipped"
    })
  }
  if (runFailed && !published && !outcome && !stepStates.includes("failed")) {
    const firstPending = stepStates.indexOf("pending")
    if (firstPending >= 0) stepStates[firstPending] = "failed"
  }

  const activeIndex = stepStates.indexOf("active")
  const failedIndex = stepStates.indexOf("failed")
  const currentStepIndex = activeIndex >= 0 ? activeIndex
    : failedIndex >= 0 ? failedIndex
      : stepStates.findIndex((state) => state === "pending") >= 0 ? stepStates.findIndex((state) => state === "pending")
        : PULL_REQUEST_WORKFLOW_STEPS.length - 1
  const settledCount = stepStates.filter((state) => state === "done" || state === "skipped").length

  const steps = PULL_REQUEST_WORKFLOW_STEPS.map((step, index) => ({
    ...step,
    state: stepStates[index]
  }))

  const lanes = CANDIDATE_LANES.map((lane, index) => {
    const original = candidates.get(lane.id) || { id: lane.id, role: lane.role }
    const originalState = outcome ? "skipped" : candidateNodeState({ generating: false, ...original }, selectedId)
    const laneNodes = [ {
      id: lane.id,
      kind: "candidate",
      title: pullRequestCandidateRoleLabels[original.role] || pullRequestCandidateRoleLabels[lane.role],
      caption: `Candidate ${index + 1}`,
      state: originalState,
      statusLine: outcome ? "Not needed" : candidateStatusLine(original, originalState)
    } ]
    const simplified = candidates.get(`${lane.id}-simplified`)
    if (simplified) {
      const simplifiedState = outcome ? "skipped" : candidateNodeState({ generating: false, ...simplified }, selectedId)
      laneNodes.push({
        id: simplified.id,
        kind: "candidate",
        variant: true,
        title: "Simplified variant",
        caption: `Candidate ${index + 1} · option ${index + 4}`,
        state: simplifiedState,
        statusLine: outcome ? "Not needed" : candidateStatusLine(simplified, simplifiedState)
      })
    }
    return laneNodes
  })

  const phaseNodes = {
    triage: {
      id: "triage",
      kind: "phase",
      title: "Triage",
      caption: "Understand the risk",
      state: stepStates[0] === "done" ? "done" : stepStates[0],
      statusLine: phaseStatusLine(stepStates[0], {
        active: "Analyzing the issue…",
        done: "Complete",
        pending: preparation ? "Waiting for preparation" : "Queued",
        timedOut: phasesTimedOut.has("triage")
      })
    },
    selection: {
      id: "selection",
      kind: "phase",
      title: "Selection",
      caption: "Semantically minimal",
      state: stepStates[4] === "done" ? "done" : stepStates[4],
      statusLine: phaseStatusLine(stepStates[4], {
        active: "Comparing options…",
        done: "Option chosen",
        timedOut: phasesTimedOut.has("selection")
      })
    },
    final_verification: {
      id: "final_verification",
      kind: "phase",
      title: "Final check",
      caption: "Host verification",
      state: stepStates[5] === "done" ? "done" : stepStates[5],
      statusLine: phaseStatusLine(stepStates[5], {
        active: "Verifying the patch…",
        done: "Passed",
        timedOut: phasesTimedOut.has("final_verification")
      })
    }
  }

  const terminalContent = (() => {
    if (published) {
      return {
        id: "terminal", kind: "terminal", state: "done",
        title: pullRequest.number ? `PR #${pullRequest.number}` : "Pull request",
        caption: "Published", statusLine: "Ready for review", href: pullRequest.url || null
      }
    }
    if (outcome) {
      return {
        id: "terminal", kind: "terminal", state: "outcome",
        title: "No pull request", caption: "Completed",
        statusLine: noPullRequestOutcomeLabels[outcome] || "No pull request was created.",
        detail: diagnosis
      }
    }
    if (runFailed) {
      const recoverablePublication = Boolean(pullRequest.retryPublishPath) || publicationFailed
      return {
        id: "terminal", kind: "terminal", state: "failed",
        title: recoverablePublication ? "Publication failed" : "Run failed",
        caption: recoverablePublication ? "Branch retained" : "Stopped",
        statusLine: firstLine(pullRequest.error) || failureText || "Generation ended before a pull request was created."
      }
    }
    const publishState = stepStates[6]
    return {
      id: "terminal", kind: "terminal", state: publishState === "active" ? "active" : "pending",
      title: "Pull request", caption: "Publish",
      statusLine: publishState === "active" ? (pushed || publicationStarted ? "Creating pull request…" : "Committing and pushing…") : "Queued"
    }
  })()

  const compact = width < WORKFLOW_LAYOUT.compactBelow
  const canvasWidth = Math.min(
    Math.max(Math.floor(width), WORKFLOW_LAYOUT.minimumWidth),
    WORKFLOW_LAYOUT.maximumWidth
  )
  const pad = WORKFLOW_LAYOUT.padding
  const gap = WORKFLOW_LAYOUT.gap
  const nodes = []
  let cursorY = pad

  const place = (node, x, y, w, h) => {
    const placed = { ...node, x, y, w, h }
    nodes.push(placed)
    return placed
  }

  if (compact) {
    const nodeWidth = canvasWidth - pad * 2
    place(phaseNodes.triage, pad, cursorY, nodeWidth, WORKFLOW_LAYOUT.phaseHeight)
    cursorY += WORKFLOW_LAYOUT.phaseHeight + gap
    lanes.forEach((lane) => {
      lane.forEach((laneNode) => {
        place(laneNode, pad, cursorY, nodeWidth, WORKFLOW_LAYOUT.compactCandidateHeight)
        cursorY += WORKFLOW_LAYOUT.compactCandidateHeight + WORKFLOW_LAYOUT.rowGap
      })
    })
    cursorY += gap - WORKFLOW_LAYOUT.rowGap
    place(phaseNodes.selection, pad, cursorY, nodeWidth, WORKFLOW_LAYOUT.phaseHeight)
    cursorY += WORKFLOW_LAYOUT.phaseHeight + gap
    place(phaseNodes.final_verification, pad, cursorY, nodeWidth, WORKFLOW_LAYOUT.phaseHeight)
    cursorY += WORKFLOW_LAYOUT.phaseHeight + gap
    place(terminalContent, pad, cursorY, nodeWidth, WORKFLOW_LAYOUT.phaseHeight)
    cursorY += WORKFLOW_LAYOUT.phaseHeight
  } else {
    const barWidth = canvasWidth - pad * 2
    const columnGap = canvasWidth < WORKFLOW_LAYOUT.narrowBelow ? 12 : 16
    const columnWidth = Math.floor((canvasWidth - pad * 2 - columnGap * 2) / 3)
    const columnX = (index) => pad + index * (columnWidth + columnGap)
    place(phaseNodes.triage, pad, cursorY, barWidth, WORKFLOW_LAYOUT.phaseHeight)
    cursorY += WORKFLOW_LAYOUT.phaseHeight + gap
    lanes.forEach((lane, index) => {
      place(lane[0], columnX(index), cursorY, columnWidth, WORKFLOW_LAYOUT.candidateHeight)
    })
    cursorY += WORKFLOW_LAYOUT.candidateHeight
    if (lanes.some((lane) => lane.length > 1)) {
      cursorY += WORKFLOW_LAYOUT.rowGap
      lanes.forEach((lane, index) => {
        if (lane[1]) place(lane[1], columnX(index), cursorY, columnWidth, WORKFLOW_LAYOUT.candidateHeight)
      })
      cursorY += WORKFLOW_LAYOUT.candidateHeight
    }
    cursorY += gap
    place(phaseNodes.selection, pad, cursorY, barWidth, WORKFLOW_LAYOUT.phaseHeight)
    cursorY += WORKFLOW_LAYOUT.phaseHeight + gap
    place(phaseNodes.final_verification, pad, cursorY, barWidth, WORKFLOW_LAYOUT.phaseHeight)
    cursorY += WORKFLOW_LAYOUT.phaseHeight + gap
    place(terminalContent, pad, cursorY, barWidth, WORKFLOW_LAYOUT.phaseHeight)
    cursorY += WORKFLOW_LAYOUT.phaseHeight
  }

  const nodeById = new Map(nodes.map((node) => [ node.id, node ]))
  const edges = []
  const connect = (fromNode, toNode) => {
    if (!fromNode || !toNode) return
    edges.push({
      from: { x: fromNode.x + fromNode.w / 2, y: fromNode.y + fromNode.h },
      to: { x: toNode.x + toNode.w / 2, y: toNode.y }
    })
  }

  lanes.forEach((lane) => {
    connect(nodeById.get("triage"), nodeById.get(lane[0].id))
    if (lane[1]) connect(nodeById.get(lane[0].id), nodeById.get(lane[1].id))
    connect(nodeById.get(lane[lane.length - 1].id), nodeById.get("selection"))
  })
  connect(nodeById.get("selection"), nodeById.get("final_verification"))
  connect(nodeById.get("final_verification"), nodeById.get("terminal"))

  return {
    steps,
    nodes,
    edges,
    width: canvasWidth,
    height: cursorY + pad,
    compact,
    narrow: !compact && canvasWidth < WORKFLOW_LAYOUT.narrowBelow,
    currentStepIndex,
    progress: settledCount / PULL_REQUEST_WORKFLOW_STEPS.length,
    preparation: phasesStarted.size === 0 ? preparation : null
  }
}

function workflowProgressLabel(model) {
  if (model.preparation && model.steps[0]?.state === "pending") return `Preparing — ${model.preparation.label}`
  const step = model.steps[model.currentStepIndex]
  if (model.steps.every((entry) => entry.state === "done" || entry.state === "skipped")) {
    return "Workflow complete"
  }
  if (step?.state === "failed") return `Stopped at step ${model.currentStepIndex + 1} of ${model.steps.length} — ${step.label}`
  return `Step ${model.currentStepIndex + 1} of ${model.steps.length} — ${step?.label || "Working"}`
}

function WorkflowNode({ node }) {
  const className = [
    "issue-pr-workflow-node",
    `is-${node.kind || "phase"}`,
    `is-${node.state || "pending"}`,
    node.variant ? "is-variant" : ""
  ].filter(Boolean).join(" ")
  const content = (
    <>
      <span className="issue-pr-workflow-node-heading">
        <span className="issue-pr-workflow-node-dot" aria-hidden="true" />
        <strong>{node.title}</strong>
      </span>
      <span className="issue-pr-workflow-node-caption">{node.caption}</span>
      <span className="issue-pr-workflow-node-status">{node.statusLine}</span>
    </>
  )
  const style = { left: `${node.x}px`, top: `${node.y}px`, width: `${node.w}px`, minHeight: `${node.h}px` }

  if (node.href) {
    return <a className={className} style={style} title={node.statusLine} href={node.href} target="_blank" rel="noopener noreferrer">{content}</a>
  }
  return <div className={className} style={style} title={node.statusLine}>{content}</div>
}

export function PullRequestWorkflowGraph({ className = "", events = [], generating = false, pullRequest = {} }) {
  const markerId = useId()
  const scrollRef = useRef(null)
  const [availableWidth, setAvailableWidth] = useState(null)

  useEffect(() => {
    const element = scrollRef.current
    if (!element || typeof ResizeObserver !== "function") return undefined

    const observer = new ResizeObserver((entries) => {
      const nextWidth = Math.floor(entries[0]?.contentRect?.width || 0)
      if (nextWidth > 0) setAvailableWidth(nextWidth)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const model = useMemo(
    () => buildPullRequestWorkflowModel(events, pullRequest, { width: availableWidth || WORKFLOW_LAYOUT.defaultWidth }),
    [ events, pullRequest, availableWidth ]
  )

  const progressLabel = workflowProgressLabel(model)
  const progressPercent = Math.round(model.progress * 100)

  return (
    <div className={`issue-pr-workflow ${className}`.trim()} data-pr-workflow>
      <div className="issue-pr-workflow-header">
        {generating ? <LoadingOrb state="composing" /> : <GitPullRequest aria-hidden="true" strokeWidth={1.8} />}
        <span className="issue-pr-workflow-step-label">{progressLabel}</span>
        <span className="issue-pr-workflow-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progressPercent} aria-label="Pull request generation progress">
          <span className="issue-pr-workflow-progress-bar" style={{ width: `${progressPercent}%` }} />
        </span>
      </div>
      <div className="issue-pr-workflow-scroll" ref={scrollRef}>
        <div
          className={`issue-pr-workflow-canvas ${model.compact ? "is-compact" : ""} ${model.narrow ? "is-narrow" : ""}`.trim()}
          role="img"
          aria-label={`Pull request generation workflow. ${progressLabel}.`}
          style={{ width: `${model.width}px`, height: `${model.height}px` }}
        >
          <svg className="issue-pr-workflow-edges" viewBox={`0 0 ${model.width} ${model.height}`} preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <marker id={markerId} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M 0 0 L 8 4 L 0 8 z" />
              </marker>
            </defs>
            {model.edges.map((edge, index) => (
              <line
                key={`edge-${index}`}
                x1={edge.from.x}
                y1={edge.from.y}
                x2={edge.to.x}
                y2={edge.to.y}
                markerEnd={`url(#${markerId})`}
              />
            ))}
          </svg>
          {model.nodes.map((node) => (
            <WorkflowNode key={node.id} node={node} />
          ))}
        </div>
      </div>
    </div>
  )
}
